import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Check, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Reveal } from "../components/Reveal";
import PageHero from "../components/PageHero";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const steps = [
  { n: "01", t: "Enquire", d: "Submit the enquiry form with your details and program of interest." },
  { n: "02", t: "Counselling", d: "Our admissions team connects with you to guide eligibility & documents." },
  { n: "03", t: "Documentation", d: "Submit academic records and complete the verification process." },
  { n: "04", t: "Enrolment", d: "Confirm your seat and begin your nursing journey with SVS." },
];

export default function Admissions() {
  const [courses, setCourses] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", phone: "", course: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get("/courses").then((r) => setCourses(r.data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.course) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/enquiries", form);
      setDone(true);
      toast.success("Enquiry submitted! Our team will reach out soon.");
      setForm({ name: "", email: "", phone: "", course: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="admissions-page">
      <PageHero
        overline="Admissions 2026-27"
        title={<>Your seat in the <span className="italic text-gold-gradient">future</span> of care</>}
        subtitle="Admissions are now open for B.Sc Nursing, GNM and ANM. Submit an enquiry and our counsellors will guide you at every step."
      />

      <section className="bg-sand py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-16">
          <div>
            <Reveal>
              <span className="text-xs tracking-[0.3em] uppercase text-gold-muted font-mono">The Process</span>
              <h2 className="font-serif text-4xl text-navy mt-4 mb-10">How to apply</h2>
            </Reveal>
            <div className="space-y-8">
              {steps.map((s, i) => (
                <Reveal key={s.n} delay={i * 0.08}>
                  <div className="grid grid-cols-[auto,1fr] gap-6 border-t border-navy/10 pt-6">
                    <div className="font-serif text-4xl text-gold/40">{s.n}</div>
                    <div>
                      <h3 className="font-serif text-xl text-navy mb-1">{s.t}</h3>
                      <p className="text-navy/60 text-sm leading-relaxed">{s.d}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          <Reveal>
            <div className="bg-white border border-navy/10 rounded-[2px] p-8 lg:p-10 shadow-[0_20px_60px_rgba(10,30,77,0.08)]">
              {done ? (
                <div className="text-center py-16" data-testid="enquiry-success">
                  <div className="mx-auto h-16 w-16 rounded-full bg-gold/15 flex items-center justify-center mb-6">
                    <Check className="text-gold" size={32} />
                  </div>
                  <h3 className="font-serif text-2xl text-navy mb-3">Thank you!</h3>
                  <p className="text-navy/60 mb-8">Your enquiry has been received. Our admissions team will contact you shortly.</p>
                  <button
                    onClick={() => setDone(false)}
                    data-testid="another-enquiry-btn"
                    className="text-gold font-semibold hover:underline"
                  >
                    Submit another enquiry
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-5" data-testid="enquiry-form">
                  <h3 className="font-serif text-2xl text-navy mb-2">Admission Enquiry</h3>
                  <div>
                    <label className="text-sm font-medium text-navy/70">Full Name *</label>
                    <Input
                      data-testid="enquiry-name"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="Your name"
                      className="mt-1.5"
                    />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-navy/70">Email *</label>
                      <Input
                        data-testid="enquiry-email"
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        placeholder="you@email.com"
                        className="mt-1.5"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-navy/70">Phone *</label>
                      <Input
                        data-testid="enquiry-phone"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        placeholder="+91 ..."
                        className="mt-1.5"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-navy/70">Program of Interest *</label>
                    <Select value={form.course} onValueChange={(v) => setForm({ ...form, course: v })}>
                      <SelectTrigger data-testid="enquiry-course" className="mt-1.5">
                        <SelectValue placeholder="Select a program" />
                      </SelectTrigger>
                      <SelectContent>
                        {courses.map((c) => (
                          <SelectItem key={c.id} value={c.title}>{c.title}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-navy/70">Message</label>
                    <Textarea
                      data-testid="enquiry-message"
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      placeholder="Tell us about your interest..."
                      className="mt-1.5"
                      rows={4}
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="enquiry-submit"
                    className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-navy text-sand font-semibold px-6 py-4 transition-transform duration-300 hover:scale-[1.02] hover:bg-navy-light disabled:opacity-60"
                  >
                    {submitting ? <Loader2 size={18} className="animate-spin" /> : "Submit Enquiry"}
                  </button>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
